/**
 * /academy/live — live sessions with a trainer (Google Meet, or Jitsi Meet as a fallback).
 * The learner registers; the server gives the link only to registered learners and only from the
 * opening of the room to the end: the « Join » button appears then (the list is re-read every
 * minute so it shows up without reloading the page).
 */

import {
  ApiError,
  type LearnerLiveSession,
  getAcademyLiveListQueryKey,
  useAcademyLiveList,
  useAcademyLiveRegisterCreate,
  useAcademyLiveRegisterDestroy,
  useAcademyMeRetrieve,
} from "@agencyos/api-client";
import { liveAction, liveWhen } from "@agencyos/core";
import { Badge, Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, UserCheck, UserMinus, UserPlus, Video } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { examParams, useActiveExam } from "./ielts/exam";
import { isSkill } from "./types";
import { SkillIcon } from "./ui";

function errorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.detail) return error.detail;
  return error ? fallback : "";
}

function SessionCard({ session, hasAccess }: { session: LearnerLiveSession; hasAccess: boolean }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: getAcademyLiveListQueryKey() });
  const register = useAcademyLiveRegisterCreate({ mutation: { onSuccess: refresh } });
  const unregister = useAcademyLiveRegisterDestroy({ mutation: { onSuccess: refresh } });
  const [confirming, setConfirming] = useState(false);
  const when = liveWhen(session.starts_at, i18n.language);
  const opens = liveWhen(session.join_opens_at, i18n.language);
  const next = liveAction(session, hasAccess);
  const full = next === "full";
  const busy = register.isPending || unregister.isPending;
  const error = errorText(register.error ?? unregister.error, t("academyLive.failed"));
  const titleId = `live-${session.id}`;

  const start = new Date(session.starts_at);
  const locale = i18n.language.startsWith("en") ? "en-GB" : "fr-FR";
  const day = Number.isNaN(start.getTime()) ? "" : start.toLocaleDateString(locale, { day: "2-digit", timeZone: "Africa/Douala" });
  const month = Number.isNaN(start.getTime()) ? "" : start.toLocaleDateString(locale, { month: "short", timeZone: "Africa/Douala" });
  const skill = session.skill && isSkill(session.skill) ? session.skill : null;

  return (
    <li>
      <GlassCard as="article" className={`ac-card ac-live${next === "join" ? " ac-live--now" : ""}`} aria-labelledby={titleId}>
        <div className="ac-live__date" aria-hidden="true">
          <span className="ac-live__month">{month}</span>
          <span className="ac-live__day ao-num">{day}</span>
        </div>
        <div className="ac-live__body">
          <div className="ac-live__badges">
            {next === "join" ? (
              <span className="ac-live__now">
                <span className="ac-rec-dot" aria-hidden="true" />
                {t("academy.ui.live.now")}
              </span>
            ) : null}
            {session.skill ? <Badge>{t(`academyLive.skill.${session.skill}`)}</Badge> : null}
            <Badge>{t(`academyLive.provider.${session.provider}`)}</Badge>
            {session.registered ? (
              <Badge tone="success">
                <UserCheck aria-hidden size={12} /> {t("academyLive.registered")}
              </Badge>
            ) : null}
            {full ? <Badge tone="danger">{t("academyLive.full")}</Badge> : null}
          </div>
          <h2 id={titleId} className="ac-live__title">
            {skill ? <SkillIcon skill={skill} size={15} /> : null}
            <span>{session.title}</span>
          </h2>
          <p className="ac-live__when">
            <CalendarClock aria-hidden size={15} />{" "}
            {t("academyLive.when", { date: when.date, time: when.time, minutes: session.duration_minutes })}
          </p>
          {session.trainer_name ? <p className="ac-secondary">{t("academyLive.trainer", { name: session.trainer_name })}</p> : null}
          {session.theme ? (
            <p className="ac-live__theme" lang="fr">
              {session.theme}
            </p>
          ) : null}
          {session.description ? <p className="ac-footnote">{session.description}</p> : null}
          {!session.registered ? <p className="ac-caption ac-live__seats">{t("academyLive.seats", { count: session.seats_left })}</p> : null}
          {next === "soon" ? (
            <p className="ac-footnote" role="status">
              {t("academyLive.joinSoon", { date: opens.date, time: opens.time })}
            </p>
          ) : null}
          {error ? (
            <p className="ko" role="alert">
              {error}
            </p>
          ) : null}
          <div className="ac-card__actions ac-card__actions--start">
            {next === "join" && session.join_url ? (
              <a className="ao-button ao-button--primary" href={session.join_url} target="_blank" rel="noopener noreferrer">
                <Video aria-hidden size={18} />
                {t("academyLive.join")}
              </a>
            ) : null}
            {session.registered ? (
              confirming ? (
                <>
                  <Button
                    variant="secondary"
                    icon={<UserMinus aria-hidden size={18} />}
                    disabled={busy}
                    onClick={() => unregister.mutate({ id: session.id }, { onSettled: () => setConfirming(false) })}
                  >
                    {t("academyLive.confirmUnregister")}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirming(false)}>
                    {t("academyLive.keep")}
                  </Button>
                </>
              ) : (
                <Button variant="ghost" onClick={() => setConfirming(true)}>
                  {t("academyLive.unregister")}
                </Button>
              )
            ) : (
              <Button
                variant="secondary"
                icon={<UserPlus aria-hidden size={18} />}
                disabled={next !== "register" || busy}
                onClick={() => register.mutate({ id: session.id })}
              >
                {busy ? t("common.loading") : t("academyLive.register")}
              </Button>
            )}
          </div>
        </div>
      </GlassCard>
    </li>
  );
}

export function LivePage() {
  const { t } = useTranslation();
  const { exam } = useActiveExam();
  const me = useAcademyMeRetrieve({ query: { retry: false } });
  const list = useAcademyLiveList(examParams(exam), { query: { retry: false, refetchInterval: 60_000 } });
  const learner = me.data?.status === 200 ? me.data.data : undefined;
  const rows = list.data?.status === 200 ? list.data.data : [];
  const hasAccess = exam === "tcf_canada" ? Boolean(learner?.has_access) : learner?.access[exam] === true;

  return (
    <div className="ac-page">
      <header className="ac-head">
        <div className="ac-head__text">
          <p className="ac-eyebrow">
            {exam !== "tcf_canada" ? `${t(`ielts.exams.${exam}.name`)} · ` : ""}
            {t("academy.ui.live.eyebrow")}
          </p>
          <h1 id="live-title" className="ac-head__title">
            {t("academyLive.title")}
          </h1>
          <div className="ac-head__subtitle">
            <p>{t("academyLive.intro")}</p>
          </div>
        </div>
      </header>
      {learner && !hasAccess ? (
        <p className="ac-note" role="note">
          {t("academyLive.needPlan")}
        </p>
      ) : null}
      {list.isPending ? (
        <div className="ac-live-list">
          <Skeleton style={{ height: 180 }} />
          <Skeleton style={{ height: 180 }} />
        </div>
      ) : null}
      {list.isError ? <p className="ko">{t("common.error")}</p> : null}
      {list.isSuccess && !rows.length ? (
        <div className="ac-surface ac-empty">
          <Video aria-hidden size={22} />
          <p className="ac-secondary">{t("academyLive.none")}</p>
        </div>
      ) : null}
      {rows.length ? (
        <ul className="ac-live-list" aria-label={t("academyLive.list")}>
          {rows.map((session) => (
            <SessionCard key={session.id} session={session} hasAccess={hasAccess} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
